import React, { useEffect, useRef, useState } from 'react';
import Layout from '../Layout/Layout.js';
import DataTable from 'react-data-table-component';
import { Button, Form } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import { APIUrl } from '../../Helper/APIUrl.js';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'link'
];

const parseJSON = async (response) => {
  try {
    if (response.status === 204 || response.status === 205) {
      return null;
    }

    const text = await response.text();
    if (!text) {
      return null;
    }

    return JSON.parse(text);
  }
  catch (error) {
    console.warn('Failed to parse JSON response', error);
    return null;
  }
};

const getPlainTextFromHtml = (html) => {
  if (!html) {
    return '';
  }

  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
};

function AddFAQ() {
  const token = JSON.parse(localStorage.getItem('tcn'));
  const localStorageUserId = JSON.parse(localStorage.getItem('userid'));
  const authToken = typeof token === 'string' ? token : token?.token || token?.Token || '';

  const { enqueueSnackbar } = useSnackbar();
  const { quill, quillRef } = useQuill({ modules: quillModules, formats: quillFormats });

  const [search, setSearch] = useState('');
  const [faqList, setFaqList] = useState([]);
  const [filteredFaqList, setFilteredFaqList] = useState([]);
  const [typeList, setTypeList] = useState([]);

  const [faqId, setFaqId] = useState(0);
  const [typeId, setTypeId] = useState(0);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [value, setValue] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [createdBy] = useState(localStorageUserId || 0);
  const [createdOn, setCreatedOn] = useState('');
  const [modifyBy] = useState(localStorageUserId || 0);
  const [modifiedOn, setModifiedOn] = useState('');
  const [edit, setEdit] = useState(false);

  const questionRef = useRef(null);

  const resetForm = () => {
    setFaqId(0);
    setTypeId(0);
    setQuestion('');
    setAnswer('');
    setValue('');
    setIsActive(true);
    setCreatedOn('');
    setModifiedOn('');
    setEdit(false);

    if (quill) {
      quill.setText('');
    }
  };

  const getFAQTypes = async () => {
    try {
      const response = await fetch(APIUrl.SZType + 'GetSZTypeBySectionId/20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({})
      });

      const result = await parseJSON(response);
      const data = Array.isArray(result?.data) ? result.data : [];
      const activeTypes = data.filter((item) => item.IsActive !== false);
      setTypeList(activeTypes.sort((a, b) => (b.TypeId || 0) - (a.TypeId || 0)));
    }
    catch (error) {
      console.log(error);
      setTypeList([]);
    }
  };

  const getAllFAQs = async () => {
    try {
      const response = await fetch(APIUrl.WEBFAQ + 'GetAllWEBFAQ', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({})
      });

      const result = await parseJSON(response);
      const data = Array.isArray(result?.data) ? result.data : [];
      const sorted = [...data].sort((a, b) => (b.Id || b.FAQId || 0) - (a.Id || a.FAQId || 0));
      setFaqList(sorted);
      setFilteredFaqList(sorted);
    }
    catch (error) {
      console.log(error);
      setFaqList([]);
      setFilteredFaqList([]);
    }
  };

  useEffect(() => {
    getFAQTypes();
    getAllFAQs();
  }, []);

  useEffect(() => {
    if (!quill) {
      return;
    }

    const handleChange = () => {
      const currentHtml = quill.root.innerHTML;
      setAnswer(currentHtml === '<p><br></p>' ? '' : currentHtml);
    };

    quill.on('text-change', handleChange);

    return () => {
      quill.off('text-change', handleChange);
    };
  }, [quill]);

  useEffect(() => {
    if (!quill) {
      return;
    }

    const nextHtml = answer || '';
    if (quill.root.innerHTML !== nextHtml) {
      quill.root.innerHTML = nextHtml;
    }
  }, [answer, quill]);

  useEffect(() => {
    const term = search.toLowerCase().trim();
    if (!term) {
      setFilteredFaqList(faqList);
      return;
    }

    const result = faqList.filter((item) => {
      const typeName = item.TypeName || item.SectionName || '';
      const questionText = item.Question || '';
      const answerText = getPlainTextFromHtml(item.Answer || '');

      return (
        typeName.toLowerCase().includes(term) ||
        questionText.toLowerCase().includes(term) ||
        answerText.toLowerCase().includes(term)
      );
    });

    setFilteredFaqList(result);
  }, [search, faqList]);

  const handleEditFAQ = (row) => {
    const faqRecord = row || {};

    setFaqId(faqRecord.Id || faqRecord.FAQId || 0);
    setTypeId(faqRecord.TypeId || 0);
    setQuestion(faqRecord.Question || '');
    setAnswer(faqRecord.Answer || '');
    setValue(faqRecord.Value || faqRecord.Question || '');
    setIsActive(faqRecord.IsActive ?? true);
    setCreatedOn(faqRecord.CreatedOn || '');
    setModifiedOn(faqRecord.ModifiedOn || '');
    setEdit(true);

    window.scrollTo({ top: 0, behavior: 'smooth' });
    questionRef.current?.focus();
  };

  const buildPayload = () => {
    const now = new Date().toISOString();

    return {
      Id: faqId,
      TypeId: Number(typeId) || 0,
      Question: question.trim(),
      Answer: answer,
      Value: value.trim() || question.trim(),
      IsActive: isActive,
      CreatedBy: createdBy || 0,
      CreatedOn: createdOn || now,
      ModifyBy: modifyBy || 0,
      ModifiedOn: now
    };
  };

  const validateFAQ = () => {
    if (!authToken) {
      enqueueSnackbar('Please login first.', { variant: 'warning' });
      return false;
    }

    if (!typeId || Number(typeId) === 0) {
      enqueueSnackbar('Select Type.', { variant: 'warning' });
      return false;
    }

    if (!question.trim()) {
      enqueueSnackbar('Enter Question.', { variant: 'warning' });
      questionRef.current?.focus();
      return false;
    }

    if (!getPlainTextFromHtml(answer)) {
      enqueueSnackbar('Enter Answer.', { variant: 'warning' });
      quill?.focus();
      return false;
    }

    return true;
  };

  const insertFAQ = async () => {
    if (!validateFAQ()) {
      return;
    }

    try {
      const payload = buildPayload();
      const response = await fetch(APIUrl.WEBFAQ + 'InsertWEBFAQ', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });

      const result = await parseJSON(response);
      if (result?.Message === 'Success' || result?.returnID !== undefined) {
        enqueueSnackbar(result?.Message || 'Successfully Added', { variant: 'success' });
        await getAllFAQs();
        resetForm();
        return;
      }

      enqueueSnackbar(result?.Message || 'Failed to save FAQ.', { variant: 'error' });
    }
    catch (error) {
      console.log(error);
      enqueueSnackbar(error?.message || 'Error while saving FAQ.', { variant: 'error' });
    }
  };

  const updateFAQ = async () => {
    if (!validateFAQ()) {
      return;
    }

    if (!faqId) {
      enqueueSnackbar('FAQ id is missing for update.', { variant: 'warning' });
      return;
    }

    try {
      const payload = buildPayload();
      const response = await fetch(APIUrl.WEBFAQ + 'UpdateWEBFAQ/' + faqId, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });

      const result = await parseJSON(response);
      if (result?.Message === 'Success' || result?.returnID !== undefined) {
        enqueueSnackbar(result?.Message || 'Successfully Updated', { variant: 'success' });
        await getAllFAQs();
        resetForm();
        return;
      }

      enqueueSnackbar(result?.Message || 'Failed to update FAQ.', { variant: 'error' });
    }
    catch (error) {
      console.log(error);
      enqueueSnackbar(error?.message || 'Error while updating FAQ.', { variant: 'error' });
    }
  };

  const activateDeactivateFAQ = async (id, valueText) => {
    try {
      const response = await fetch(APIUrl.WEBFAQ + 'ActivateDeactivateWEBFAQ/' + id, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ Value: valueText })
      });

      const result = await parseJSON(response);
      if (result) {
        await getAllFAQs();
        enqueueSnackbar(valueText === 'activate' ? 'Successfully Activated' : 'Successfully Deactivated', {
          variant: valueText === 'activate' ? 'success' : 'warning'
        });
      }
    }
    catch (error) {
      console.log(error);
      enqueueSnackbar('Failed to update FAQ status.', { variant: 'error' });
    }
  };

  const columns = [
    {
      name: 'SNo.',
      selector: (row, index) => index + 1,
      width: '80px'
    },
    {
      name: 'Head',
      selector: (row) => row.TypeName || row.SectionName || row.TypeId,
      sortable: true,
      grow: 1
    },
    {
      name: 'Question',
      selector: (row) => row.Question,
      sortable: true,
      grow: 2
    },
    {
      name: 'Answer',
      selector: (row) => getPlainTextFromHtml(row.Answer || '').slice(0, 120),
      grow: 3
    },
    {
      name: 'Status',
      selector: (row) => row.IsActive
        ? <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Activated</label>
        : <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Deactivated</label>
    },
    {
      name: 'Action',
      cell: (row) => (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => handleEditFAQ(row)}
            className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2"
          >
            <i className="fa fa-pencil" aria-hidden="true"></i>
          </Button>
          {
            row.IsActive
              ?
              <Button
                type="button"
                onClick={() => activateDeactivateFAQ(row.Id || row.FAQId, 'deactivate')}
                className="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-4 py-2"
              >
                <i className="fa fa-toggle-on" aria-hidden="true"></i>
              </Button>
              :
              <Button
                type="button"
                onClick={() => activateDeactivateFAQ(row.Id || row.FAQId, 'activate')}
                className="text-white bg-gradient-to-r from-red-400 via-red-500 to-red-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-4 py-2"
              >
                <i className="fa fa-toggle-off" aria-hidden="true"></i>
              </Button>
          }
        </div>
      )
    }
  ];

  return (
    <div>
      <Layout>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Add FAQ</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <Form.Select
                  value={typeId}
                  onChange={(e) => setTypeId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                >
                  <option value="0">Select Type</option>
                  {
                    typeList.map((item) => (
                      <option key={item.TypeId} value={item.TypeId}>
                        {item.TypeName || item.Value || item.TypeId}
                      </option>
                    ))
                  }
                </Form.Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                <input
                  type="text"
                  ref={questionRef}
                  value={question}
                  onChange={(e) => {
                    setQuestion(e.target.value);
                    setValue(e.target.value);
                  }}
                  placeholder="Enter question"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <div className="overflow-hidden rounded-lg border border-gray-300 bg-white">
                    <div ref={quillRef} className="min-h-[220px] bg-white" />
                  </div>
                </div>

              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-3">
                {
                  edit ? (
                    <Button
                      type="button"
                      onClick={updateFAQ}
                      className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center space-x-2"
                    >
                      Update
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={insertFAQ}
                      className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center space-x-2"
                    >
                      Add
                    </Button>
                  )
                }
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <DataTable
                  title="Manage FAQ"
                  columns={columns}
                  data={filteredFaqList}
                  pagination
                  fixedHeader
                  fixedHeaderScrollHeight="550px"
                  highlightOnHover
                  subHeader
                  subHeaderAlign="right"
                  noDataComponent={<div className="py-8 text-sm text-slate-500">No FAQ records found.</div>}
                  subHeaderComponent={
                    <input
                      type="text"
                      placeholder="search question"
                      className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  }
                />
              </div>
            </div>
          </div>

        </main>
      </Layout>
    </div>
  );
}

export default AddFAQ;
